import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  CartesianViewportHandle,
  CartesianViewportView,
  FigureElement,
  FigureHandle,
  FormulaLegendHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { createCartesianViewport, createFormulaLegend, createSvgEl } from '@learnforge/demo-render';
import { parse, evaluate } from '@learnforge/demo-math/expr';
import { clamp, formatNumber } from '@learnforge/demo-math/numeric';
import type { CalculusContext } from '../types';
import type { CalcKey } from '../locales/keys';
import { createCurveLayer, type CurveLayerHandle } from '../render/curve';
import { createDualPane, type DualPaneHandle } from '../render/dual-pane';
import { isPointInCartesianDomain } from '../render/cartesian-plane';
import { compileFn, sampleFn, riemannSum } from '../calc';
import { INTEGRAL_PRESETS, type IntegralPreset } from '../data/integral-presets';

// ─── Stage layout constants ───────────────────────────────────────────────────

const STAGE_W = 800;
const STAGE_H = 460;
const PANE_GAP = 20;
/** Number of sample points per curve. */
const SAMPLE_N = 400;
/** Number of subintervals for numeric area via Riemann sum. */
const RIEMANN_N = 2000;
/** World-unit padding on each side of the sampled y-range. */
const Y_PAD = 0.5;

// ─── State ────────────────────────────────────────────────────────────────────

export interface FtcLabState {
  presetId: string;
  x: number;
  showSecondFtc: boolean;
}

// ─── Exported pure helpers (unit-tested) ─────────────────────────────────────

/**
 * Compute the exact accumulation value `A(x) = F(x) − F(a)` from the curated
 * antiderivative and the numeric area fill `∫ₐˣ f` via a fine Riemann sum.
 *
 * Both should agree (within ε) per the Fundamental Theorem of Calculus.
 */
export function accumulationModel(state: FtcLabState): { areaUpToX: number; aOfX: number } {
  const preset = INTEGRAL_PRESETS.find((p) => p.id === state.presetId) ?? INTEGRAL_PRESETS[0]!;
  const { a, x } = getAandX(preset, state.x);

  const FNode = parse(preset.F).node;
  const aOfX = evaluate(FNode, { x }) - evaluate(FNode, { x: a });

  const f = compileFn(preset.f);
  // When x === a the integral is zero; riemannSum over a degenerate interval returns 0.
  const areaUpToX = x === a ? 0 : riemannSum(f, a, x, RIEMANN_N, 'mid');

  return { areaUpToX, aOfX };
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/** Return the preset's left endpoint `a` and the clamped cursor `x`. */
function getAandX(preset: IntegralPreset, stateX: number): { a: number; x: number } {
  const a = preset.a;
  const x = Math.max(a, Math.min(preset.b, stateX));
  return { a, x };
}

/**
 * Sample `f` over `[x0, x1]` and return `[min, max]` of finite y-values,
 * padded by `pad` world units.  Always includes 0 so the axis/area baseline shows.
 */
function sampleYRange(
  f: (x: number) => number,
  x0: number,
  x1: number,
  n: number,
  pad: number,
): [number, number] {
  const pts = sampleFn(f, x0, x1, n);
  let yMin = 0;
  let yMax = 0;
  for (const p of pts) {
    if (Number.isFinite(p.y)) {
      if (p.y < yMin) yMin = p.y;
      if (p.y > yMax) yMax = p.y;
    }
  }
  const span = yMax - yMin;
  const extra = Math.max(pad, span * 0.15);
  return [yMin - extra, yMax + extra];
}

// ─── Spec factory ─────────────────────────────────────────────────────────────

function buildSpec(): ExperimentSpec<FtcLabState, CalcKey, CalculusContext> {
  // Closure-stashed handles created in setup():
  let dual: DualPaneHandle;
  let topCurves: CurveLayerHandle;
  let bottomCurves: CurveLayerHandle;
  let figure: FigureHandle;
  let readout: ReadoutHandle;
  let viewport: CartesianViewportHandle;
  let view: CartesianViewportView;
  let legend: FormulaLegendHandle;
  let topBaseY: [number, number];
  let bottomBaseY: [number, number];
  let activePresetId = INTEGRAL_PRESETS[0]!.id;
  // Managed SVG <g> for the area-fill polygon; recreated each render.
  let areaGroup: SVGGElement | null = null;

  function defaultState(_ctx: CalculusContext): FtcLabState {
    const preset = INTEGRAL_PRESETS[0]!;
    const mid = (preset.a + preset.b) / 2;
    return {
      presetId: preset.id,
      x: mid,
      showSecondFtc: false,
    };
  }

  function applyPresetLocal(state: FtcLabState, id: string): FtcLabState {
    const preset = INTEGRAL_PRESETS.find((p) => p.id === id);
    if (!preset) return state;
    const mid = (preset.a + preset.b) / 2;
    return { ...state, presetId: preset.id, x: mid };
  }

  function getPreset(state: FtcLabState): IntegralPreset {
    return INTEGRAL_PRESETS.find((p) => p.id === state.presetId) ?? INTEGRAL_PRESETS[0]!;
  }

  function clearAreaGroup(): void {
    if (areaGroup) {
      areaGroup.remove();
      areaGroup = null;
    }
  }

  function fittedDomains(preset: IntegralPreset) {
    const f = compileFn(preset.f);
    const FNode = parse(preset.F).node;
    const aFn = (x: number) => evaluate(FNode, { x }) - evaluate(FNode, { x: preset.a });
    return {
      xDomain: [preset.a, preset.b] as [number, number],
      topYDomain: sampleYRange(f, preset.a, preset.b, SAMPLE_N, Y_PAD),
      bottomYDomain: sampleYRange(aFn, preset.a, preset.b, SAMPLE_N, Y_PAD),
    };
  }

  function mappedYDomain(base: [number, number], normalized: [number, number]): [number, number] {
    const span = base[1] - base[0];
    return [base[0] + normalized[0] * span, base[0] + normalized[1] * span];
  }

  function applyView(next: CartesianViewportView): void {
    view = next;
    dual.setDomains({
      xDomain: next.xDomain,
      topYDomain: mappedYDomain(topBaseY, next.yDomain),
      bottomYDomain: mappedYDomain(bottomBaseY, next.yDomain),
    });
  }

  return {
    labelKey: 'experiment.ftc-lab',
    demoId: 'calculus',
    advancedKey: 'ftc-lab',

    initialState(ctx): FtcLabState {
      return defaultState(ctx);
    },

    applyPreset(state, id): FtcLabState {
      return applyPresetLocal(state, id);
    },

    setup(ctx: CalculusContext, controller: HarnessController<FtcLabState>): void {
      const preset = INTEGRAL_PRESETS[0]!;
      const fitted = fittedDomains(preset);
      const { xDomain, topYDomain, bottomYDomain } = fitted;
      topBaseY = topYDomain;
      bottomBaseY = bottomYDomain;

      dual = createDualPane(controller.svg, {
        xDomain,
        topYDomain,
        bottomYDomain,
        width: STAGE_W,
        height: STAGE_H,
        gap: PANE_GAP,
      });

      // Two curve layers — one per pane.
      topCurves = createCurveLayer(dual.topContent, dual.top);
      controller.registerCleanup(() => topCurves.destroy());

      bottomCurves = createCurveLayer(dual.bottomContent, dual.bottom);
      controller.registerCleanup(() => bottomCurves.destroy());

      // Draw axes once (they get refreshed by setDomains each render).
      dual.drawAxes();

      figure = controller.createFigure();
      readout = controller.createReadout();
      legend = createFormulaLegend(ctx.root);
      controller.registerCleanup(() => legend.destroy());
      viewport = createCartesianViewport(controller.svg, {
        base: { xDomain, yDomain: [0, 1] },
        plotRect: { x: 0, y: 0, width: STAGE_W, height: STAGE_H },
        onChange(next) {
          applyView(next);
          controller.requestRender();
        },
      });
      view = viewport.getView();
      controller.registerCleanup(() => viewport.dispose());

      controller.registerCleanup(() => {
        clearAreaGroup();
        dual.destroy();
      });
    },

    render(state: FtcLabState, ctx): void {
      const preset = getPreset(state);
      if (preset.id !== activePresetId) {
        const fitted = fittedDomains(preset);
        activePresetId = preset.id;
        topBaseY = fitted.topYDomain;
        bottomBaseY = fitted.bottomYDomain;
        viewport.setBase({ xDomain: fitted.xDomain, yDomain: [0, 1] }, { notify: false });
        view = viewport.getView();
        applyView(view);
      }
      const { a, x } = getAandX(preset, state.x);
      const [xMin, xMax] = view.xDomain;

      const f = compileFn(preset.f);
      const FNode = parse(preset.F).node;
      const aFn = (xi: number) => evaluate(FNode, { x: xi }) - evaluate(FNode, { x: a });

      // ── Top pane: f curve ──
      const fPts = sampleFn(f, xMin, xMax, SAMPLE_N);
      topCurves.setPolyline('f', fPts, 'calc-curve-primary');

      // ── Top pane: shaded accumulated area from a to x ──
      clearAreaGroup();

      if (x > a) {
        const steps = Math.max(2, Math.round(SAMPLE_N * ((x - a) / (preset.b - preset.a))));
        const areaPts = sampleFn(f, a, x, steps);

        // Polygon: curve points forward, then back along the baseline (y=0 in world).
        const screenCurvePts = areaPts.map((p) => dual.top.worldToScreen(p));
        const baselineY = dual.top.worldToScreen({ x: 0, y: 0 }).y;
        const firstX = screenCurvePts[0]!.x;
        const lastX = screenCurvePts[screenCurvePts.length - 1]!.x;

        const polyPoints: Vec2[] = [
          { x: firstX, y: baselineY },
          ...screenCurvePts,
          { x: lastX, y: baselineY },
        ];

        const pointsStr = polyPoints.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');

        areaGroup = createSvgEl('g');
        areaGroup.setAttribute('class', 'calc-area-fill');
        const poly = createSvgEl('polygon');
        poly.setAttribute('class', 'calc-area');
        poly.setAttribute('points', pointsStr);
        areaGroup.appendChild(poly);
        // Insert before topCurves.group so shading is behind the f curve.
        dual.topContent.insertBefore(areaGroup, topCurves.group);
      }

      // ── Bottom pane: A(x) curve ──
      const aPts = sampleFn(aFn, xMin, xMax, SAMPLE_N);
      bottomCurves.setPolyline('A', aPts, 'calc-curve-secondary');

      // ── Accumulation model for readout and dot placement ──
      const { aOfX, areaUpToX } = accumulationModel(state);

      // ── Figure elements ──
      // pt-x: draggable, positioned on the shared x-axis in the top pane.
      const ptXPoint = { x, y: 0 };
      const ptXScreen = dual.top.worldToScreen(ptXPoint);

      // tie-dot: moving dot at (x, A(x)) in the bottom pane.
      const aOfXFinite = Number.isFinite(aOfX) ? aOfX : 0;
      const tieDotPos = dual.bottom.worldToScreen({ x, y: aOfXFinite });

      // Vertical guide through cursor x (full stage height).
      const guideTopPt: Vec2 = { x: ptXScreen.x, y: 0 };
      const guideBotPt: Vec2 = { x: ptXScreen.x, y: STAGE_H };

      const elements: FigureElement[] = [];
      if (isPointInCartesianDomain(ptXPoint, dual.top.getDomains())) {
        elements.push({
          kind: 'point',
          id: 'pt-x',
          at: ptXScreen,
          style: { className: 'geo-point-draggable' },
          draggable: {},
        });
      }
      elements.push(
        // Vertical dashed guide through x.
        {
          kind: 'point',
          id: 'guide-top',
          at: guideTopPt,
          style: { className: 'geo-point-off-stage' },
        },
        {
          kind: 'point',
          id: 'guide-bot',
          at: guideBotPt,
          style: { className: 'geo-point-off-stage' },
        },
        {
          kind: 'segment',
          id: 'guide-line',
          from: 'guide-top',
          to: 'guide-bot',
          style: { className: 'geo-line geo-line--faint', dashed: true },
        },
      );
      if (isPointInCartesianDomain({ x, y: aOfXFinite }, dual.bottom.getDomains())) {
        elements.push(
          // Moving tie-dot at (x, A(x)) in the bottom pane.
          {
            kind: 'point',
            id: 'tie-dot',
            at: tieDotPos,
            style: { className: 'geo-point-main' },
          },
        );
      }

      figure.update(elements);
      topCurves.group.ownerSVGElement
        ?.querySelector('[data-figure-id="pt-x"]')
        ?.setAttribute('data-no-pan', 'true');

      const fAtA = evaluate(FNode, { x: preset.a });
      const accumulationFormula =
        Math.abs(fAtA) < 1e-12
          ? preset.F
          : `${preset.F} ${fAtA < 0 ? '+' : '−'} ${formatNumber(Math.abs(fAtA), { decimals: 4 })}`;
      legend.setEntries([
        { id: 'f', formula: `f(x) = ${preset.f}`, color: 'var(--accent)' },
        { id: 'accumulation', formula: `A(x) = ${accumulationFormula}`, color: 'var(--ink-soft)' },
      ]);

      // ── Readout ──
      const rows = buildReadout(preset, x, aOfX, areaUpToX, ctx.advanced && state.showSecondFtc);
      readout.update(rows);
    },

    onDrag(state: FtcLabState, pointId: string, at: Vec2): FtcLabState {
      if (pointId === 'pt-x') {
        const preset = getPreset(state);
        const worldX = dual.top.screenToWorld(at).x;
        const clamped = clamp(worldX, preset.a, preset.b);
        return { ...state, x: clamped };
      }
      return state;
    },

    panel(state: FtcLabState, ctx): PanelRow<CalcKey, FtcLabState>[] {
      const rows: PanelRow<CalcKey, FtcLabState>[] = [];

      // Preset dropdown.
      rows.push({
        kind: 'dropdown',
        id: 'ftc-preset',
        labelKey: 'ftc.panel.preset',
        options: INTEGRAL_PRESETS.map((p) => ({ value: p.id, labelKey: p.labelKey })),
        value: state.presetId,
        onChange: (v) => applyPresetLocal(state, v),
      });

      // x slider.
      const preset = getPreset(state);
      rows.push({
        kind: 'slider',
        id: 'ftc-x',
        labelKey: 'ftc.panel.x',
        min: preset.a,
        max: preset.b,
        step: (preset.b - preset.a) / 100,
        value: state.x,
        format: (v) => v.toFixed(2),
        onChange: (v) => ({ ...state, x: clamp(v, preset.a, preset.b) }),
      });

      // Advanced toggle.
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      // Advanced: second FTC toggle.
      if (ctx.advanced) {
        rows.push({
          kind: 'toggle',
          labelKey: 'ftc.panel.show-second-ftc',
          value: state.showSecondFtc,
          onChange: (v) => ({ ...state, showSecondFtc: v }),
        });
      }

      return rows;
    },

    actions: (_state: FtcLabState, ctx): ActionBarSpec<CalcKey, FtcLabState> => {
      return {
        onReset: () => defaultState(ctx),
        onRandomise: (s) => {
          const idx = Math.floor(ctx.prng() * INTEGRAL_PRESETS.length);
          const preset = INTEGRAL_PRESETS[idx]!;
          const mid = (preset.a + preset.b) / 2;
          return { ...s, presetId: preset.id, x: mid };
        },
        presets: INTEGRAL_PRESETS.map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id),
        viewReset: { labelKey: 'action.reset-view', onResetView: () => viewport.reset() },
      };
    },

    applyShotState(state: FtcLabState, _shotId: string): FtcLabState {
      // Pin x to mid-domain so shaded area and tie-dot are clearly visible.
      const preset = getPreset(state);
      const mid = (preset.a + preset.b) / 2;
      return { ...state, x: mid };
    },
  };
}

// ─── Readout builder ──────────────────────────────────────────────────────────

function buildReadout(
  preset: IntegralPreset,
  x: number,
  aOfX: number,
  areaUpToX: number,
  showSecondFtc: boolean,
): ReadoutRow[] {
  const rows: ReadoutRow[] = [
    // Curated antiderivative expression.
    {
      id: 'antiderivative',
      lhs: 'F(x)',
      relation: '=',
      rhs: preset.F,
      emphasis: 'invariant',
    },
    // The FTC identity: A′(x) = f(x).
    {
      id: 'ftc-identity',
      lhs: 'A′(x)',
      relation: '=',
      rhs: preset.f,
      emphasis: 'invariant',
    },
    // Numeric accumulation A(x) = F(x) − F(a).
    {
      id: 'a-of-x',
      lhs: 'A(x)',
      relation: '=',
      rhs: formatNumber(aOfX, { decimals: 4 }),
      emphasis: 'neutral',
    },
    // Current x value.
    {
      id: 'x-value',
      lhs: 'x',
      relation: '=',
      rhs: formatNumber(x, { decimals: 4 }),
      emphasis: 'neutral',
    },
  ];

  // Advanced: Second FTC and numeric area vs exact comparison.
  if (showSecondFtc) {
    rows.push({
      id: 'numeric-area',
      lhs: '∫ₐˣ f (numeric)',
      relation: '≈',
      rhs: formatNumber(areaUpToX, { decimals: 4 }),
      emphasis: 'neutral',
    });
    const err = Math.abs(areaUpToX - aOfX);
    rows.push({
      id: 'ftc-error',
      lhs: '|area − A|',
      relation: '=',
      rhs: formatNumber(err, { decimals: 4 }),
      emphasis: err < 0.01 ? 'invariant' : 'neutral',
    });
  }

  return rows;
}

export const experiment = defineExperiment(buildSpec());
